import type { AGGError } from '@jira/platform__graphql-relay-errors/src/index.tsx';

/**
 * Error subclass for network failures during GraphQL operations.
 *
 * Provides structural parity with Apollo's ApolloError so that callers
 * can use the same catch-block logic regardless of the GraphQL client:
 *
 *  - `error instanceof Error`          → true
 *  - `error.networkError`              → the underlying transport error
 *  - `error.graphQLErrors`             → always [] for network errors
 *  - `error.type`                      → 'network' (backward compatibility)
 *  - `error.statusCode`               → HTTP status when available
 *  - `error.originalError`            → alias for networkError (backward compat)
 */
export class RelayNetworkError extends Error {
	readonly type = 'network' as const;

	readonly statusCode?: number;

	readonly originalError: Error;

	readonly networkError: Error;

	readonly graphQLErrors: AGGError[] = [];

	constructor(error: Error) {
		super(error.message);
		this.name = 'NetworkError';

		this.originalError = error;
		this.networkError = error;
		this.statusCode =
			'statusCode' in error && typeof error.statusCode === 'number' ? error.statusCode : undefined;
	}
}

export const createRelayNetworkError = (error: Error): RelayNetworkError =>
	new RelayNetworkError(error);
